import { useState } from 'react';
import { Check } from 'lucide-react';

import {
  BillingInterval,
  GetViewerAvailableProgramPlansQuery,
} from '@sandtech-hub-monorepo/ehub-client';
import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';

import { plural } from '../../../utils/plural';
import { formatAmount, getYearlySavingPercent } from './planPricing';

/**
 * One plan as it comes off `GetViewerAvailableProgramPlans`. Named for the
 * query it is cut from rather than for the thing it describes: ehub-client already generates
 * `LearnerBillingPlan` for the schema type, and a local `BillingPlan` beside
 * it would read as a second definition of the same thing while actually being
 * this query's selection set.
 */
type AvailableProgramPlansQueryPlan = NonNullable<
  GetViewerAvailableProgramPlansQuery['viewer']['availablePrograms']
>[number]['billingPlans'][number];

/** A plan plus the two things the page derives rather than reads. */
export interface PurchasablePlan extends AvailableProgramPlansQueryPlan {
  /** How many GSA programs this plan unlocks, across the whole catalogue. */
  programCount: number;
  /**
   * A program this plan is known to cover, for the checkout mutation — which
   * requires one and refuses a plan that does not cover it. The learner never
   * sees it.
   */
  checkoutProgramId: string;
}

/**
 * Fixed marketing copy. The backend holds a plan's name, prices and the
 * programs it covers, and nothing else, so there is no per-plan feature list
 * to read — these lines apply to every paid plan. They are deliberately
 * program-agnostic: one plan covers a set of programs, so "all 6 courses" was
 * describing one program's course list rather than the plan.
 *
 * Monthly and yearly commit to different things, so the cover line and the
 * feature list both differ by interval: yearly promises coverage for a full
 * year of catalogue growth instead of counting today's programs, and drops
 * "Cancel anytime" (the note below already covers cancellation terms).
 */
const INTERVAL_PLAN_COPY: Record<
  BillingInterval,
  { coverLine: (programCount: number) => string; features: (programCount: number) => string[] }
> = {
  [BillingInterval.Month]: {
    coverLine: (programCount) =>
      `Full access to all ${programCount} ${plural(programCount, 'AI program', 'AI programs')}`,
    features: () => [
      '15 modules of AI skills building',
      'Access to community',
      'Hands-on exercises & guided demos',
      'Earn skills badges and certificates of completion',
      'Cancel anytime',
    ],
  },
  [BillingInterval.Year]: {
    coverLine: () => 'Unlock unlimited learning for 1 year',
    features: (programCount) => [
      `Access all ${programCount} ${plural(programCount, 'AI program', 'AI programs')}, plus any new program as they are added throughout the year`,
      'Access to community',
      'Hands-on exercises & guided demos',
      'Earn skills badges and certificates of completion',
    ],
  },
};

// Key order is the toggle's order: cheapest commitment first, so it reads left
// to right the way the prices do.
const INTERVAL_COPY: Record<BillingInterval, { toggle: string; per: string }> =
  {
    [BillingInterval.Month]: { toggle: 'Monthly', per: 'per month' },
    [BillingInterval.Year]: { toggle: 'Yearly', per: 'per year' },
  };

interface Props {
  plan: PurchasablePlan;
  onSelect: (interval: BillingInterval) => void;
  className?: string;
}

/**
 * One purchasable plan: its name, the intervals it is actually sold on, and
 * the CTA into checkout.
 *
 * Only the interval crosses to checkout alongside the plan id — never an
 * amount. The url is user-editable, so anything it carried about money would
 * be a "charge me $1" hole; the server resolves the price from the plan.
 */
export const PlanCard = ({ plan, onSelect, className }: Props) => {
  const monthly = plan.prices.find((p) => p.interval === BillingInterval.Month);
  const yearly = plan.prices.find((p) => p.interval === BillingInterval.Year);
  const intervals = [monthly, yearly]
    .filter((p): p is PurchasablePlan['prices'][number] => !!p)
    .map((p) => p.interval);

  // Yearly is the default when it is sold, being the lower monthly rate of the
  // two. A plan sold on one interval has no toggle at all rather than a toggle
  // with one dead half.
  const [chosen, setChosen] = useState<BillingInterval | null>(null);
  const interval = chosen ?? (yearly ? BillingInterval.Year : intervals[0]);

  const price = interval === BillingInterval.Year ? yearly : monthly;
  const saving = getYearlySavingPercent(
    monthly?.unitAmount,
    yearly?.unitAmount
  );
  const copy = INTERVAL_PLAN_COPY[interval];

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl bg-primary p-8 text-banner-subheading',
        className
      )}
    >
      <h2 className="text-center text-xl font-semibold">{plan.name}</h2>

      {intervals.length > 1 && (
        <div className="mt-4 flex justify-center">
          <div className="inline-flex items-center rounded-full bg-white/20 p-1 text-sm">
            {intervals.map((candidate) => (
              <button
                key={candidate}
                type="button"
                onClick={() => setChosen(candidate)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-4 py-1',
                  candidate === interval
                    ? 'bg-white text-primary'
                    : 'text-banner-subheading'
                )}
              >
                {INTERVAL_COPY[candidate].toggle}
                {candidate === BillingInterval.Year && saving !== undefined && (
                  <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-banner-subheading">
                    -{saving}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/*
        `unitAmount` is nullable and is a cache the server refreshes at
        checkout, so a null renders as an em dash. Falling back to zero would
        put "$0.00" on a card that is about to charge.
      */}
      <p className="mt-4 text-center text-5xl font-bold">
        {price?.unitAmount != null
          ? formatAmount(price.unitAmount, price.currency)
          : '—'}
      </p>
      <p className="text-center text-sm text-banner-subheading/80">
        {price?.unitAmount != null
          ? INTERVAL_COPY[interval].per
          : 'Your price is shown at checkout'}
      </p>

      {/*
        What the plan covers. Monthly states it as a count rather than a list —
        the page never names a program, and a list stops being readable the
        moment the catalogue grows — while yearly promises the year's coverage
        instead of today's count, since a program added mid-year is covered too.
      */}
      <p className="mt-6 border-t border-white pb-2 pt-2 text-center text-sm font-semibold">
        {copy.coverLine(plan.programCount)}
      </p>

      <ul className="flex flex-1 flex-col gap-3 border-t border-white pt-4">
        {copy.features(plan.programCount).map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-foreground/20">
              <Check className="h-3.5 w-3.5" />
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <p className="mt-4 text-center text-xs text-primary-foreground-muted">
        Note: Annual subscriptions cannot be switched to monthly. You can
        cancel at any time.
      </p>

      <Button
        disabled={!price}
        onClick={() => price && onSelect(price.interval)}
        className="mt-4 w-full rounded-full bg-white text-primary hover:bg-white/90"
      >
        Subscribe to All Access
      </Button>
      <p className="mt-2 text-center text-xs text-primary-foreground-muted">
        Secure and encrypted payment
      </p>
    </div>
  );
};
